/**
 * Where a night battery's searchlights are pointing — the aiming law, with no
 * `three` in it, so every angle in this file is a unit test rather than a
 * screenshot.
 *
 * ## PORT EXTENSION, and an unusually clean-cut one
 *
 * The original draws **nothing** like this. The searches that settle it, run
 * over the floppy tree and all eight CD volumes:
 *
 * - zero occurrences of `srchlite`, `searchli` or `spotlight` in any volume;
 * - no asset whose name contains `SRCH`, `BEAM` or `LAMP` anywhere in
 *   `RESOURCE.000`-`.007`;
 * - the one file that looks like a hit, `LIGHT.WND`, is a **light wind** model
 *   — 74 bytes, one of the four stubs `missions-campaign.md` §`.WND` records,
 *   and not a lamp;
 * - `RB.EXE` holds no `night`, `dusk`, `dawn` or `moon` literal at all, which
 *   `time-of-day.ts` had already found from the other direction.
 *
 * So there is no art to be faithful to and no code to recover. What there *is*
 * is the manual's own history, which describes the thing this module draws:
 * German bombers "started flying only at night" once the day defences bit, and
 * "new antiaircraft guns were put into service from London to the coast"
 * against them (`docs/game/manual/02-historical-overview.md`); the Zeppelin
 * raids Londoners feared came on "moonlit nights", and a burning airship fell
 * "from a darkened sky" (ibid.). A WWI anti-aircraft position after dark was
 * guns *and* lights — the gun cannot lay on what the crew cannot see. This
 * module puts the lights on the batteries the sim already has, and nowhere
 * else.
 *
 * It is gated on **the hour and nothing else** (`scene-searchlights.ts`'s
 * `searchlightsLit`). It briefly rode the SKY chip, back when one of that
 * chip's states drew a night mission as the flat measured daytime picture and a
 * beam would have had nothing to be in; with the treatment collapsed to a
 * bi-state, every sky a player can fly under draws a night as a night, and the
 * hour is mission data rather than a setting.
 *
 * ## Night is the sim's own test, not a string comparison
 *
 * The gate is `sunDirectionAt(timeOfDay) === undefined`, which is
 * `time-of-day.ts`'s spelling of `sun.c` `0xf9f5`'s `sin(sunAngle) < 0` — the
 * original's own day/night switch, the one that hides the sun and un-hides the
 * moon. Asking it that way rather than `timeOfDay === "night"` means the beams
 * light up under exactly the condition the recovered law calls night, and if a
 * later lane gives `CELESTIAL_HOURS` a real clock the lights follow it for
 * free. Today the two agree: of the four buckets only `night` puts the sun
 * under the horizon (dusk's 17:30 still has it 7 degrees up).
 *
 * ## What the beams do
 *
 * **Idle: a seeded scissor sweep.** Azimuth and elevation each ride their own
 * sine, at periods that are coprime (23 s and 17 s) so a pair does not settle
 * into a repeating figure, with the phase, the resting bearing and a small
 * period stretch all drawn from `fnv1a32` of the battery id and the beam index.
 * Deterministic in the sim's own `timeS`, so two runs of one mission sweep
 * identically and a test can assert an angle.
 *
 * **Tracking: the sim's truth, not a guess.** `flak.ts` already keeps, per gun,
 * the aircraft that gun is laid on (`FlakSlot.targetId`) and how well it has
 * ranged in (`trackingLevel`). When a battery has guns up, its beams take those
 * targets — one each when the battery is split across two aeroplanes, both on
 * the same one when it is not, which is `aa.c`'s own concentration behaviour
 * made visible. The beam does not decide anything; it *shows* what the gunnery
 * already decided, which is why being caught in one means something.
 *
 * **Wrecked: the light goes out and the lamp stays.** Since AD-017 §11 every
 * beam stands on a real entity — a `searchlight` `EncounterTarget` with
 * integrity, strafeable with the same rounds everything else is
 * (`combat/searchlight-posts.ts`). A post that has been shot out publishes an
 * entry with `intensity` 0, which the beam pool refuses outright, and a
 * `wrecked` flag the mount pool draws as a toppled projector. So the collection
 * is not "the beams"; it is "the lights, working or not", and the difference
 * between the two states is the whole feedback for having shot one.
 *
 * **The swing is rate-limited either way.** A searchlight is a tonne of mirror
 * and carbon arc on a hand-cranked mount, so both the sweep and the pounce are
 * fed through one slew limiter at `MAX_SLEW_DEG_PER_S`. The sweep is slower
 * than the limit and so passes through untouched; a target that appears across
 * the sky is swung onto over about a second. One mechanism, so losing a target
 * swings the beam back to its sweep rather than snapping it.
 */

import {
  SEARCHLIGHTS_PER_BATTERY,
  SEARCHLIGHT_KIND,
  searchlightBatteryId,
  searchlightLit,
  searchlightPostIndex,
  targetProfileOf,
  trackingLevel,
  type EncounterState,
  type EncounterTarget,
  type FlakBattery,
  type TimeOfDay,
} from "@red-opus/engine";
import { sunDirectionAt } from "./time-of-day.ts";
import {
  ACQUIRING_INTENSITY,
  HELD_INTENSITY,
  IDLE_INTENSITY,
  beamSeed,
  seededUnit,
  slewBeam,
  sweepAim,
  type AimVector,
  type SearchlightBeam,
} from "./searchlight-sweep.ts";

// The aiming arithmetic lives next door (`searchlight-sweep.ts`) and travels
// with the policy, so a consumer that wants "the searchlights" still has one
// import. The split is a size ceiling, not an API boundary.
export {
  ACQUIRING_INTENSITY,
  AZIMUTH_PERIOD_S,
  AZIMUTH_SWEEP_DEG,
  ELEVATION_CENTRE_DEG,
  ELEVATION_PERIOD_S,
  ELEVATION_SWEEP_DEG,
  HELD_INTENSITY,
  IDLE_INTENSITY,
  MAX_SEARCHLIGHTS,
  MAX_SLEW_DEG_PER_S,
  beamSeed,
  emptySearchlightBeam,
  seededUnit,
  slewBeam,
  sweepAim,
} from "./searchlight-sweep.ts";
export type { AimVector, SearchlightBeam } from "./searchlight-sweep.ts";

/**
 * The four fields of `EncounterState` the beams actually read, named as a
 * `Pick` the way `frame-signals.ts`'s `FireRunner` is. Narrow because it is
 * honest — nothing here can reach the rounds, the bursts or the outcome — and
 * because it lets a test aim a beam without standing up a whole encounter.
 *
 * `targets` joined the list when the lights became shootable (AD-017 §11): a
 * beam no longer *belongs* to a battery, it belongs to a searchlight post
 * standing near one, and the post is an `EncounterTarget` with integrity like
 * anything else on the ground.
 */
export type SearchlightState = Pick<EncounterState, "flak" | "aircraft" | "targets" | "timeS">;

/**
 * Whether this mission's briefed hour is one the lights come on at, asked as
 * `sun.c` `0xf9f5` asks it: the sun is under the horizon.
 * `sunDirectionAt` returns `undefined` for two different reasons — the sun has
 * set, *or* there is no briefed hour at all (a dogfight, a duel, a replay) —
 * and only the first is night, so the bucket is checked for existence first.
 * Without that, every duel would be flown under searchlights.
 */
export function isNight(timeOfDay: TimeOfDay | undefined): boolean {
  return timeOfDay !== undefined && sunDirectionAt(timeOfDay) === undefined;
}

/**
 * Beams per emplacement. Two, because one light sweeping alone reads as a
 * stray effect and a *pair* reads as a defended position — and because the
 * moment the two converge on one aeroplane is the whole point of the feature.
 * The port places 2-3 batteries in a mission (`mission/flak-placement.ts` puts
 * one belt battery per side, plus an objective gun on balloon scenes), so this
 * is 4-6 beams in the sky, not the eighteen-battery hedge the original's live
 * capture counted.
 *
 * Re-exported from the engine rather than restated, now that each beam has a
 * *post* under it: the two numbers were the same number all along, and one of
 * them owning both is what stops a mission ever placing three lamps and drawing
 * two beams.
 */
export const BEAMS_PER_BATTERY = SEARCHLIGHTS_PER_BATTERY;

/**
 * The distinct aircraft this battery's five guns are laid on, in slot order, up
 * to `BEAMS_PER_BATTERY`. Slot order is `aa.c`'s own, so which beam takes which
 * aeroplane is as deterministic as the gunnery that assigned them.
 *
 * Written into a caller-owned array so a frame's collection allocates nothing.
 */
function trackedTargets(battery: FlakBattery, into: (string | null)[]): number {
  let count = 0;
  for (const slot of battery.slots) {
    const id = slot.targetId;
    if (id === null) continue;
    let seen = false;
    for (let i = 0; i < count; i++) if (into[i] === id) seen = true;
    if (seen) continue;
    into[count] = id;
    count += 1;
    if (count === BEAMS_PER_BATTERY) break;
  }
  return count;
}

/** The best-ranged-in gun this battery has on `targetId`, as 0, 1 or 2 — the light steadies as the shells close. */
function bestTrackingLevel(battery: FlakBattery, targetId: string): number {
  let best = 0;
  for (const slot of battery.slots) {
    if (slot.targetId !== targetId) continue;
    const level = trackingLevel(slot);
    if (level > best) best = level;
  }
  return best;
}

/** Scratch for one frame's collection. Module-level, so `collectSearchlights` never allocates. */
const scratchAim: AimVector = { x: 0, y: 1, z: 0 };
const scratchTargets: (string | null)[] = Array.from({ length: BEAMS_PER_BATTERY }, () => null);

/**
 * The emplacement's own ground, from the post's centred pose.
 *
 * `combat/targets.ts` stands a ground target at `groundY + centreHeightM` so
 * that its collision sphere sits at its middle, while a searchlight *mount* is
 * drawn standing on its feet. One subtraction, taken from the profile rather
 * than written down here, so a change to how tall the projector is cannot leave
 * the beam hanging in the air over it.
 */
const POST_CENTRE_HEIGHT_M = targetProfileOf(SEARCHLIGHT_KIND).centreHeightM;

/**
 * Aims every beam in the mission for this frame and returns how many entries
 * were published — live beams *and* wrecks, since a shot-out mount is still
 * hardware to draw.
 *
 * Writes into `into`, which the caller owns and never reallocates. The beams
 * carry their own slewed direction between frames, so this both *decides* the
 * aim and *advances* towards it — the two cannot be separated without keeping a
 * second copy of the state.
 *
 * The walk is over the **posts** (AD-017 §11), not over the batteries: a beam
 * exists because a lamp is standing there, and its battery is looked up from
 * the post's own id. A mission with batteries and no posts — every daylight
 * sortie, every duel, every replay of a tape made before the lights were
 * shootable — collects nothing, which is exactly the empty pool it collected
 * before.
 *
 * `dt` of 0 is meaningful and used: the kill camera re-draws the same instant
 * through a second lens, and a beam must not move because it was looked at
 * twice.
 */
export function collectSearchlights(state: SearchlightState, dt: number, into: SearchlightBeam[]): number {
  let count = 0;
  for (const post of state.targets) {
    if (post.kind !== SEARCHLIGHT_KIND) continue;
    if (count === into.length) return count;
    const beam = into[count];
    if (beam === undefined) return count;
    const battery = batteryOf(state, post);
    if (battery === undefined) continue;
    count += 1;
    aimPost(state, battery, post, beam, dt);
  }
  return count;
}

/** The battery a post serves, or `undefined` if its emplacement has gone from the roster. */
function batteryOf(state: SearchlightState, post: EncounterTarget): FlakBattery | undefined {
  const batteryId = searchlightBatteryId(post.id);
  if (batteryId === null) return undefined;
  return state.flak.find((entry) => entry.id === batteryId);
}

/** One post's beam for one frame: where it stands, whether it still works, and where it is looking. */
function aimPost(
  state: SearchlightState,
  battery: FlakBattery,
  post: EncounterTarget,
  beam: SearchlightBeam,
  dt: number,
): void {
  const beamIndex = searchlightPostIndex(post.id) ?? 0;
  beam.originX = post.position.x;
  beam.originY = post.position.y - POST_CENTRE_HEIGHT_M;
  beam.originZ = post.position.z;
  beam.seed = beamSeed(battery.id, beamIndex);

  if (!searchlightLit(post)) {
    beam.wrecked = true;
    beam.tracking = false;
    // Zero is what the pool reads as "draw no beam" — `SearchlightBeams.push`
    // refuses an intensity of zero outright, so a dead lamp costs no instance
    // rather than costing an invisible one.
    beam.intensity = 0;
    // Seeded off the beam's own sweep seed, so every wreck on the field fell a
    // different way and none of them re-falls when the frame rate changes.
    beam.wreckBearingRad = seededUnit(beam.seed, 4) * 2 * Math.PI;
    return;
  }

  beam.wrecked = false;
  const targetCount = trackedTargets(battery, scratchTargets);
  // Both beams take the one aeroplane when the battery is concentrated on
  // one, which is `aa.c`'s normal state — five guns pile onto a lone
  // target — and one each when it has split.
  const targetId = targetCount === 0 ? null : (scratchTargets[beamIndex % targetCount] ?? null);
  // Resolved against `state.aircraft` rather than the renderer's own
  // `others()`, because that view drops the player's own aeroplane — and
  // the player is precisely whom a battery is usually laid on.
  const target = targetId === null ? undefined : state.aircraft.find((entry) => entry.id === targetId);

  if (target === undefined) {
    beam.tracking = false;
    beam.intensity = IDLE_INTENSITY;
    sweepAim(beam.seed, state.timeS, scratchAim);
  } else {
    beam.tracking = true;
    const level = targetId === null ? 0 : bestTrackingLevel(battery, targetId);
    beam.intensity = ACQUIRING_INTENSITY + ((HELD_INTENSITY - ACQUIRING_INTENSITY) * level) / 2;
    const dx = target.sim.position.x - beam.originX;
    const dy = target.sim.position.y - beam.originY;
    const dz = target.sim.position.z - beam.originZ;
    const length = Math.hypot(dx, dy, dz);
    if (length < 1e-6) {
      sweepAim(beam.seed, state.timeS, scratchAim);
    } else {
      scratchAim.x = dx / length;
      scratchAim.y = dy / length;
      scratchAim.z = dz / length;
    }
  }

  // A beam that has never been aimed starts *on* its sweep rather than
  // slewing there from the straight-up it was constructed at — otherwise
  // every light in the mission would swing down from the zenith together in
  // the first second, which is the one moment they should look settled.
  if (!beam.aimed) {
    beam.dirX = scratchAim.x;
    beam.dirY = scratchAim.y;
    beam.dirZ = scratchAim.z;
    beam.aimed = true;
  } else {
    slewBeam(beam, scratchAim, dt);
  }
}
